import { create } from 'zustand';
import type { Comment, Approval, ApprovalStatus } from '@/types/timeline.ts';
import { useUndoStore } from './undoStore.ts';
import { useProjectStore } from './projectStore.ts';
import { useAccountStore } from './accountStore.ts';


interface CommentState {
  comments: Comment[];
  approvals: Approval[];
  reviewerName: string;

  // Comment actions
  addComment: (shotId: number | null, timecode: number | null, text: string, parentId?: number, annotationSvg?: string | null, screenplayPage?: number | null, sceneId?: number | null) => void;
  updateComment: (id: number, text: string) => void;
  deleteComment: (id: number) => void;
  deleteAllCommentsForShot: (shotId: number) => void;
  getCommentsForShot: (shotId: number) => Comment[];
  getCommentsForPage: (sceneId: number, pageNumber: number) => Comment[];

  // Approval actions
  setApproval: (shotId: number, status: ApprovalStatus, notes?: string) => void;
  getApprovalForShot: (shotId: number) => Approval | null;

  // Reviewer name
  setReviewerName: (name: string) => void;

  // Remote patch actions — called by realtime sync, no undo snapshots
  _patchRemoteComment: (comment: Comment) => void;
  _patchRemoteApproval: (approval: Approval) => void;
  _patchRemoteCommentDelete: (commentId: number) => void;
}

export const useCommentStore = create<CommentState>((set, get) => ({
  comments: [],
  approvals: [],
  reviewerName: localStorage.getItem('timeline-reviewer-name') || '',

  addComment: (shotId, timecode, text, parentId, annotationSvg, screenplayPage, sceneId) => {
    useUndoStore.getState().pushSnapshot();
    const { comments, reviewerName } = get();
    const newComment: Comment = {
      id: useProjectStore.getState()._allocId(),
      shotId,
      timecode,
      author: reviewerName,
      text,
      createdAt: new Date().toISOString(),
      resolvedAt: null,
      parentCommentId: parentId ?? null,
      annotationSvg: annotationSvg ?? null,
      screenplayPage: screenplayPage ?? null,
      sceneId: sceneId ?? null,
      userId: useAccountStore.getState().accountUser?.id ?? null,
    };
    set({ comments: [...comments, newComment] });

    // Auto-change approval from pending → notes when a comment is added
    if (shotId !== null && !parentId) {
      const approvals = get().approvals;
      const existing = approvals.filter(a => a.shotId === shotId);
      const latest = existing.length > 0
        ? existing.reduce((l, a) => a.revision > l.revision ? a : l)
        : null;
      if (!latest || latest.status === 'pending' || latest.status === 'approved') {
        const maxRevision = existing.reduce((m, a) => Math.max(m, a.revision), 0);
        const noteApproval: Approval = {
          id: useProjectStore.getState()._allocId(),
          shotId,
          status: 'notes',
          approvedBy: reviewerName,
          approvedAt: new Date().toISOString(),
          notes: '',
          revision: maxRevision + 1,
        };
        set({ approvals: [...get().approvals, noteApproval] });
      }
    }
  },

  updateComment: (id, text) => {
    useUndoStore.getState().pushSnapshot();
    set(state => ({
      comments: state.comments.map(c =>
        c.id === id ? { ...c, text } : c
      ),
    }));
  },

  deleteComment: (id) => {
    useUndoStore.getState().pushSnapshot();
    set(state => {
      const idsToDelete = new Set<number>([id]);
      let changed = true;
      while (changed) {
        changed = false;
        for (const c of state.comments) {
          if (c.parentCommentId !== null && idsToDelete.has(c.parentCommentId) && !idsToDelete.has(c.id)) {
            idsToDelete.add(c.id);
            changed = true;
          }
        }
      }
      return { comments: state.comments.filter(c => !idsToDelete.has(c.id)) };
    });
  },

  deleteAllCommentsForShot: (shotId) => {
    useUndoStore.getState().pushSnapshot();
    set(state => ({
      comments: state.comments.filter(c => c.shotId !== shotId),
    }));
  },

  getCommentsForShot: (shotId) => get().comments.filter(c => c.shotId === shotId),

  getCommentsForPage: (sceneId, pageNumber) => get().comments.filter(
    c => c.screenplayPage === pageNumber && c.sceneId === sceneId,
  ),

  setApproval: (shotId, status, notes) => {
    useUndoStore.getState().pushSnapshot();
    const { approvals, reviewerName } = get();
    const existing = approvals.filter(a => a.shotId === shotId);
    const maxRevision = existing.reduce((m, a) => Math.max(m, a.revision), 0);

    const newApproval: Approval = {
      id: useProjectStore.getState()._allocId(),
      shotId,
      status,
      approvedBy: reviewerName,
      approvedAt: new Date().toISOString(),
      notes: notes ?? '',
      revision: maxRevision + 1,
    };

    set({ approvals: [...approvals, newApproval] });
  },

  getApprovalForShot: (shotId) => {
    const approvals = get().approvals.filter(a => a.shotId === shotId);
    if (approvals.length === 0) return null;
    return approvals.reduce((latest, a) => a.revision > latest.revision ? a : latest);
  },

  setReviewerName: (name) => {
    localStorage.setItem('timeline-reviewer-name', name);
    set({ reviewerName: name });
  },

  // Remote patch actions — called by realtime sync, no undo snapshots
  _patchRemoteComment: (comment) => {
    set(state => {
      if (state.comments.some(c => c.id === comment.id)) return state;
      // Content dedup: creator comments exist locally with positive IDs but
      // arrive from Supabase with negative IDs (uuidToNegId). Match on
      // author + shotId + text + createdAt to avoid duplicating them.
      if (state.comments.some(c =>
        c.shotId === comment.shotId &&
        c.author === comment.author &&
        c.text === comment.text &&
        c.createdAt === comment.createdAt
      )) return state;
      return { comments: [...state.comments, comment] };
    });
  },

  _patchRemoteApproval: (approval) => {
    set(state => {
      // Semantic dedup: same (shotId, revision) means same logical approval
      // regardless of whether IDs differ (positive local vs negative remote)
      if (state.approvals.some(a =>
        a.id === approval.id ||
        (a.shotId === approval.shotId && a.revision === approval.revision)
      )) return state;
      return { approvals: [...state.approvals, approval] };
    });
  },

  _patchRemoteCommentDelete: (commentId) => {
    set(state => ({
      comments: state.comments.filter(c => c.id !== commentId),
    }));
  },
}));
